import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import InfoButton from '../../../../../components/InfoButton';
import Modal from '../../../../../components/Modal';
import SelectAsyncJSX from '../../../../../components/SelectAsync';
import {
  backendErrorNotification,
  frontendNotification
} from '../../../../../components/Notification';
import HttpStatus from '../../../../../enums/httpStatus';
import { datasyncAPI, datasyncTypesAPI } from '../../../../../services/requests';
import { DatasyncType, PersistedDatasync } from '../../../../../types/apiResponse/datasync';
import { SelectOption } from '../../../../../utils/getSelectOptions';

type DatasyncModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  datasync?: PersistedDatasync | null;
};

type DatasyncFormData = {
  active: boolean;
  interval_in_minutes: number;
  timeout_in_seconds: number;
  full_sync: boolean;
  datasync_type?: string;
};

const DatasyncModal = ({ open, onClose, onSuccess, datasync }: DatasyncModalProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedType, setSelectedType] = useState<SelectOption | null>(null);
  const [formData, setFormData] = useState<DatasyncFormData>({
    active: true,
    full_sync: false,
    interval_in_minutes: 1,
    timeout_in_seconds: 180,
  });

  const isEditMode = !!datasync?.id;

  useEffect(() => {
    if (open) {
      if (datasync) {
        setFormData({
          active: datasync.active,
          full_sync: datasync.full_sync,
          interval_in_minutes: datasync.interval_in_minutes,
          timeout_in_seconds: datasync.timeout_in_seconds ?? 180,
          datasync_type: datasync.datasync_type,
        });

        // Initialize selected type in edit mode
        if (datasync.type) {
          setSelectedType({
            label: `${t(datasync.type.label)} - ${datasync.type.key}`,
            value: datasync.type.key,
          });
        }
      } else {
        setFormData({
          active: true,
          full_sync: false,
          interval_in_minutes: 1,
          timeout_in_seconds: 180,
        });
        setSelectedType(null);
      }
    } else {
      setIsLoading(false);
    }
  }, [open, datasync]);

  const handleTypeChange = (option: SelectOption | null) => {
    setSelectedType(option);
    setFormData((prev) => ({ ...prev, datasync_type: option?.value as string }));
  };

  const handleSubmit = async () => {
    if (!isEditMode && !selectedType) {
      frontendNotification({
        message: `${t('select')}: ${t('type')}`,
        type: 'error'
      });
      return;
    }

    if (!formData.interval_in_minutes || formData.interval_in_minutes < 1) {
      frontendNotification({
        message: `${t('invalid')}: ${t('interval')}`,
        type: 'error'
      });
      return;
    }

    setIsLoading(true);

    try {
      if (isEditMode && datasync?.id) {
        datasyncAPI.query = '';
        const res = await datasyncAPI.update(
          {
            active: formData.active,
            full_sync: formData.full_sync,
            interval_in_minutes: formData.interval_in_minutes,
            timeout_in_seconds: formData.timeout_in_seconds,
          },
          datasync.id
        );

        if (res.status !== HttpStatus.OK) {
          throw res;
        }

        frontendNotification({
          message: t('datasyncUpdatedSuccessfully'),
          type: 'success',
        });
      } else {
        datasyncAPI.query = '';
        const res = await datasyncAPI.store({
          datasync_type: formData.datasync_type,
          active: formData.active,
          full_sync: formData.full_sync,
          interval_in_minutes: formData.interval_in_minutes,
          timeout_in_seconds: formData.timeout_in_seconds,
        });

        if (res.status !== HttpStatus.CREATED) {
          throw res;
        }

        frontendNotification({
          message: t('datasyncCreatedSuccessfully'),
          type: 'success',
        });
      }

      onSuccess();
      onClose();
    } catch (error) {
      backendErrorNotification(error as AxiosError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title={
        isEditMode
          ? `${t('edit')} ${t('datasync')}`
          : `${t('new')} ${t('datasync')}`
      }
      showModal={open}
      closeModal={onClose}
      cancelButton={onClose}
      actionNameBtn={t('save')}
      action={handleSubmit}
      isLoading={isLoading}
      style={{ width: '50rem' }}
      bodyStyle={{
        width: '100%',
        overflow: 'auto'
      }}
    >
      <div className="form">
        <div className="form-row">
          <div className="form-row-item">
            <label htmlFor="datasyncType">{t('unregisteredSynchronizers') as string}</label>
            <SelectAsyncJSX
              value={selectedType}
              onChange={handleTypeChange}
              request={datasyncTypesAPI}
              query="onlyUnregistered=true"
              reqResponseToOption={{
                mapper: {
                  label: (item: DatasyncType) => `${t(item.label)} - ${item.key}`,
                  value: (item: DatasyncType) => item.key,
                },
              }}
              placeholder={t('selectType')}
              disabled={isLoading || isEditMode}
              id="datasyncType"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-row-item">
            <label
              htmlFor="intervalInMinutes"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <span>{t('intervalInMinutes') as string}</span>
              <InfoButton msg={t('info.datasync.interval')} />
            </label>
            <input
              id="intervalInMinutes"
              type="number"
              value={formData.interval_in_minutes}
              onChange={(e) =>
                setFormData({ ...formData, interval_in_minutes: Number(e.target.value) })
              }
              disabled={isLoading}
              min={1}
            />
          </div>
          <div className="form-row-item">
            <label
              htmlFor="timeoutInSeconds"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <span>{`${t('timeout')} (${t('inSeconds')}) - Min: 60, Max: 7200`}</span>
              <InfoButton msg={t('info.datasync.timeout')} />
            </label>
            <input
              id="timeoutInSeconds"
              type="number"
              value={formData.timeout_in_seconds}
              onChange={(e) => {
                const value = Number(e.target.value);
                const clampedValue = Math.max(60, Math.min(7200, value));
                setFormData({ ...formData, timeout_in_seconds: clampedValue });
              }}
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-row-item">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                id="datasync-active"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                disabled={isLoading}
                style={{ marginRight: '0.5rem', width: 'auto' }}
              />
              <label htmlFor="datasync-active" style={{ cursor: 'pointer' }}>
                {t('active') as string}
              </label>
            </div>
          </div>
          <div className="form-row-item">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                id="datasync-full-sync"
                checked={formData.full_sync}
                onChange={(e) => setFormData({ ...formData, full_sync: e.target.checked })}
                disabled={isLoading}
                style={{ marginRight: '0.5rem', width: 'auto' }}
              />
              <label
                htmlFor="datasync-full-sync"
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                {t('fullSync') as string}
                <InfoButton msg={t('info.datasync.fullSync')} />
              </label>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

DatasyncModal.defaultProps = {
  datasync: null,
};

export default DatasyncModal;
